import { useState, useEffect, useCallback, useContext, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useHistory, useLocation } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  IconButton,
  Chip,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  Button,
  TextField,
  Switch,
  FormControlLabel,
  FormControl,
  FormHelperText,
  Autocomplete,
  CircularProgress,
} from '@mui/material';
import { DoneAll, Delete, OpenInNew, ArrowBack, Edit } from '@mui/icons-material';
import Tooltip from '@mui/material/Tooltip';
import { format } from 'date-fns';
import csLocale from 'date-fns/locale/cs';

import AuthContext from '../contexts/AuthContext';
import { useAxios } from '../hooks/useAxios';
import { useUnreadMessages } from '../hooks/useUnreadMessages';
import { isTrustedMessageLink } from '../utils/messageLink';

const SEARCH_DELAY = 300;
const RECIPIENTS_INLINE_LIMIT = 4;
/** Max outbox batches that trigger a recipients fetch on load (avoids N parallel GETs). */
const RECIPIENTS_INLINE_FETCH_CAP = 10;

const tabSx = {
  minHeight: 44,
  '& .MuiTabs-indicator': {
    backgroundColor: '#f05a5a',
    height: 2,
    borderRadius: 999,
  },
  '& .MuiTab-root': {
    color: 'var(--ink-3)',
    textTransform: 'none',
    fontWeight: 600,
    fontSize: '0.875rem',
    minHeight: 44,
  },
  '& .MuiTab-root.Mui-selected': {
    color: 'var(--ink)',
  },
};

const formatMonthLabel = (date) => {
  const label = format(date, 'LLLL yyyy', { locale: csLocale });
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const groupByMonth = (items, dateField = 'creationDate') => {
  const sorted = [...items].sort((a, b) => new Date(b[dateField]) - new Date(a[dateField]));
  const map = new Map();

  sorted.forEach((item) => {
    if (!item[dateField]) return;
    const date = new Date(item[dateField]);
    const key = format(date, 'yyyy-MM');
    if (!map.has(key)) {
      map.set(key, { label: formatMonthLabel(date), items: [] });
    }
    map.get(key).items.push(item);
  });

  return Array.from(map.entries()).sort(([a], [b]) => b.localeCompare(a));
};

const getInboxSenderLabel = (item) => item.senderName || (item.isSystem ? 'Systém' : 'Odesílatel');

const resolveMessageLink = (item) => {
  const link = item?.link || '';
  if (!link) {
    return '';
  }
  const entityType = item?.relatedEntity?.entityType;
  if (
    (entityType === 'shiftAllocation' || entityType === 'employeeAvailability') &&
    item?.relatedDate
  ) {
    return `/my-shifts?date=${format(new Date(item.relatedDate), 'yyyy-MM-dd')}`;
  }
  return link;
};

const getOutboxSenderBadge = (item) => {
  if (item.sendToAll) return 'Všem';
  const preview = item.recipientsPreview || [];
  if (preview.length > 0) return preview[0];
  if (item.recipientCount > 1) return `${item.recipientCount} příjemců`;
  if (item.recipientCount === 1) return '1 příjemce';
  return 'Odeslané';
};

const formatMessageDateTime = (dateValue) => {
  if (!dateValue) return '';
  return format(new Date(dateValue), 'dd.MM.yyyy HH:mm');
};

const MessageListRow = ({ item, senderBadge, isUnread, onClick }) => (
  <Box
    component="button"
    type="button"
    className={['glass-message-list-row', isUnread ? 'glass-message-list-row--unread' : '']
      .filter(Boolean)
      .join(' ')}
    onClick={onClick}
  >
    <Box className="glass-message-list-row-top">
      <Typography
        variant="body2"
        className="glass-message-list-row-title"
        sx={{
          fontWeight: isUnread ? 700 : 500,
          color: 'var(--ink)',
        }}
      >
        {item.title}
      </Typography>
      <Typography variant="caption" sx={{ color: 'var(--ink-3)', flexShrink: 0, ml: 1 }}>
        {formatMessageDateTime(item.creationDate)}
      </Typography>
    </Box>
    <Chip
      size="small"
      className="glass-badge-muted"
      label={senderBadge}
      sx={{ height: 22, mt: 0.5 }}
    />
  </Box>
);

const MessageListGrouped = ({ groups, getSenderBadge, isUnread, onItemClick }) => {
  if (groups.length === 0) return null;

  return (
    <Box className="glass-message-list">
      {groups.map(([monthKey, { label, items }]) => (
        <Box key={monthKey} className="glass-message-list-month">
          <Typography variant="subtitle2" className="glass-message-month-header">
            {label}
          </Typography>
          <Box className="glass-message-list-group">
            {items.map((item) => (
              <MessageListRow
                key={item.id || item._id}
                item={item}
                senderBadge={getSenderBadge(item)}
                isUnread={isUnread ? isUnread(item) : false}
                onClick={() => onItemClick(item)}
              />
            ))}
          </Box>
        </Box>
      ))}
    </Box>
  );
};

const composeBackSx = {
  mb: 2,
  px: 0,
  minHeight: 44,
  justifyContent: 'flex-start',
  color: '#f05a5a',
  textTransform: 'none',
  fontWeight: 700,
  fontSize: '1rem',
  '&:hover': { background: 'rgb(240 90 90 / 0.12)' },
};

const MessageComposeView = ({
  onBack,
  onSend,
  canSendMessages,
  isFormValid,
  isRecipientsValid,
  isRecipientsTouched,
  users,
  selectedRecipients,
  onRecipientsChange,
  recipientSearchTerm,
  onRecipientSearchChange,
  onRecipientsBlur,
  sendToAll,
  onSendToAllChange,
  isSearchingUsers,
  title,
  onTitleChange,
  message,
  onMessageChange,
}) => (
  <Box className="glass-message-detail glass-message-compose">
    <Button
      type="button"
      className="glass-message-detail-back"
      startIcon={<ArrowBack />}
      onClick={onBack}
      sx={composeBackSx}
    >
      Zpět
    </Button>

    <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--ink)', mb: 2 }}>
      Nová zpráva
    </Typography>

    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <FormControl fullWidth error={isRecipientsTouched && !isRecipientsValid}>
        <Autocomplete
          multiple
          options={users}
          value={selectedRecipients}
          onChange={(_, value) => onRecipientsChange(value || [])}
          onInputChange={(_, inputValue) => onRecipientSearchChange(inputValue)}
          getOptionLabel={(option) => option.label || ''}
          isOptionEqualToValue={(option, value) => option.value === value.value}
          disabled={sendToAll}
          loading={isSearchingUsers}
          loadingText={
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <CircularProgress color="inherit" size={16} />
              Vyhledávám uživatele...
            </span>
          }
          renderInput={(params) => (
            <TextField {...params} variant="standard" label="Příjemci" onBlur={onRecipientsBlur} />
          )}
        />
        {isRecipientsTouched && !isRecipientsValid && (
          <FormHelperText>Vyberte příjemce nebo zvolte Odeslat všem.</FormHelperText>
        )}
      </FormControl>

      <FormControlLabel
        label="Odeslat všem"
        sx={{ color: 'var(--ink-2)' }}
        control={
          <Switch
            checked={sendToAll}
            onChange={(event) => onSendToAllChange(event.target.checked)}
            color="primary"
          />
        }
      />

      <TextField
        label="Předmět"
        value={title}
        onChange={(event) => onTitleChange(event.target.value)}
        fullWidth
        size="small"
      />

      <TextField
        label="Zpráva"
        value={message}
        onChange={(event) => onMessageChange(event.target.value)}
        fullWidth
        multiline
        rows={6}
      />

      {!canSendMessages && (
        <Alert
          severity="info"
          sx={{
            backgroundColor: 'rgb(63 81 181 / 0.12)',
            color: 'var(--ink)',
            border: '1px solid rgb(63 81 181 / 0.3)',
          }}
        >
          Nemáte oprávnění odesílat zprávy.
        </Alert>
      )}

      <Button
        variant="contained"
        className="glass-accent-fill"
        onClick={onSend}
        disabled={!canSendMessages || !isFormValid || !isRecipientsValid}
        sx={{ boxShadow: '0 4px 12px rgb(240 90 90 / 0.3)', mt: 0.5 }}
      >
        Odeslat zprávu
      </Button>
    </Box>
  </Box>
);

const MessageDetailView = ({ item, source, onBack, onOpenLink, onDelete }) => {
  const isInbox = source === 'inbox';
  const senderLabel = getInboxSenderLabel(item);

  return (
    <Box className="glass-message-detail">
      <Button
        type="button"
        className="glass-message-detail-back"
        startIcon={<ArrowBack />}
        onClick={onBack}
        sx={composeBackSx}
      >
        Zpět
      </Button>

      <Typography variant="h6" sx={{ fontWeight: 700, color: 'var(--ink)', mb: 1 }}>
        {item.title}
      </Typography>

      {isInbox ? (
        <Chip
          size="small"
          className="glass-badge-muted"
          label={senderLabel}
          sx={{ height: 24, mb: 0.75 }}
        />
      ) : (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            mb: 0.75,
          }}
        >
          <Chip
            size="small"
            className="glass-badge-muted"
            label={item.senderName || 'Odesílatel'}
            sx={{ height: 24 }}
          />
          <Typography variant="caption" sx={{ color: 'var(--ink-3)', flexShrink: 0 }}>
            Přečteno {item.readCount || 0}/{item.recipientCount || 0}
          </Typography>
        </Box>
      )}

      <Typography variant="caption" sx={{ display: 'block', color: 'var(--ink-3)', mb: 2 }}>
        {formatMessageDateTime(item.creationDate)}
      </Typography>

      <Typography
        variant="body2"
        sx={{ whiteSpace: 'pre-line', color: 'var(--ink-2)', lineHeight: 1.6, mb: 3 }}
      >
        {item.message}
      </Typography>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {item.link && isTrustedMessageLink(resolveMessageLink(item)) && (
          <Button
            variant="contained"
            className="glass-accent-fill"
            startIcon={<OpenInNew />}
            onClick={() => onOpenLink(item)}
            sx={{ boxShadow: '0 4px 12px rgb(240 90 90 / 0.3)' }}
          >
            Otevřít
          </Button>
        )}
        {isInbox && (
          <Button
            variant="outlined"
            color="error"
            startIcon={<Delete />}
            onClick={() => onDelete(item.id || item._id)}
            sx={{ borderColor: 'rgb(240 90 90 / 0.4)', color: '#f05a5a' }}
          >
            Smazat
          </Button>
        )}
      </Box>
    </Box>
  );
};

const Messages = () => {
  const [messages, setMessages] = useState([]);
  const [outboxMessages, setOutboxMessages] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [selectedMessage, setSelectedMessage] = useState(null);
  const [detailSource, setDetailSource] = useState('inbox');
  const [users, setUsers] = useState([]);
  const [recipientSearchTerm, setRecipientSearchTerm] = useState('');
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [sendToAll, setSendToAll] = useState(false);
  const [isRecipientsTouched, setIsRecipientsTouched] = useState(false);
  const [title, setTitle] = useState('');
  const [message, setMessage] = useState('');
  const [isSearchingUsers, setIsSearchingUsers] = useState(false);
  const [isComposeOpen, setIsComposeOpen] = useState(false);
  const [recipientsCache, setRecipientsCache] = useState({});
  const loadedRecipientBatchIdsRef = useRef(new Set());

  const { token, masterDataPermissions, isAdmin } = useContext(AuthContext);
  const { requestStatus, sendRequest, clearRequestStatus, loadingStatus } = useAxios();
  const { refreshEventName } = useUnreadMessages();
  const history = useHistory();
  const location = useLocation();

  const filterDate = useMemo(() => {
    const dateParam = new URLSearchParams(location.search).get('date');
    return dateParam && /^\d{4}-\d{2}-\d{2}$/.test(dateParam) ? dateParam : null;
  }, [location.search]);

  const filteredMessages = useMemo(() => {
    if (!filterDate) {
      return messages;
    }

    return messages.filter((item) => {
      if (!item?.relatedDate) return false;
      return format(new Date(item.relatedDate), 'yyyy-MM-dd') === filterDate;
    });
  }, [filterDate, messages]);

  const inboxGroups = useMemo(() => groupByMonth(filteredMessages), [filteredMessages]);
  const outboxGroups = useMemo(() => groupByMonth(outboxMessages), [outboxMessages]);

  const canSendMessages = isAdmin || masterDataPermissions?.canCreate?.includes('messages');
  const isRecipientsValid = sendToAll || selectedRecipients.length > 0;
  const isFormValid = title.trim().length > 0 && message.trim().length > 0;
  const unreadCount = messages.filter((item) => !item.isRead).length;
  const canMarkAllRead = messages.length > 0 && messages.some((item) => !item.isRead);

  const getMessages = useCallback(async () => {
    try {
      const response = await sendRequest('/messages', 'GET', null, {
        Authorization: `Bearer ${token}`,
      });
      setMessages(response?.messages || []);
    } catch (error) {}
  }, [token, sendRequest]);

  const getOutboxMessages = useCallback(async () => {
    try {
      const response = await sendRequest('/messages/sent', 'GET', null, {
        Authorization: `Bearer ${token}`,
      });
      setOutboxMessages(response?.messages || []);
    } catch (error) {}
  }, [token, sendRequest]);

  useEffect(() => {
    getMessages();
    getOutboxMessages();
  }, [getMessages, getOutboxMessages]);

  useEffect(() => {
    if (filterDate) {
      setActiveTab(0);
    }
  }, [filterDate]);

  useEffect(() => {
    let cancelled = false;

    const pending = [];
    for (const item of outboxMessages) {
      if (pending.length >= RECIPIENTS_INLINE_FETCH_CAP) {
        break;
      }
      const batchId = item.id || item._id;
      if (
        !batchId ||
        item.sendToAll ||
        !(item.recipientCount > 0 && item.recipientCount <= RECIPIENTS_INLINE_LIMIT) ||
        (item.recipientsPreview || []).length >= item.recipientCount ||
        loadedRecipientBatchIdsRef.current.has(batchId)
      ) {
        continue;
      }
      pending.push(item);
    }

    const loadInlineRecipients = async () => {
      for (const batch of pending) {
        if (cancelled) {
          return;
        }
        const batchId = batch.id || batch._id;
        loadedRecipientBatchIdsRef.current.add(batchId);
        try {
          const response = await sendRequest(`/messages/sent/${batchId}/recipients`, 'GET', null, {
            Authorization: `Bearer ${token}`,
          });
          if (cancelled) {
            return;
          }
          const items = (response?.recipients || []).slice(0, RECIPIENTS_INLINE_LIMIT);
          setRecipientsCache((prev) => ({ ...prev, [batchId]: items }));
        } catch {
          loadedRecipientBatchIdsRef.current.delete(batchId);
        }
      }
    };

    loadInlineRecipients();
    return () => {
      cancelled = true;
    };
  }, [outboxMessages, sendRequest, token]);

  const getUsers = useCallback(async () => {
    try {
      if (!recipientSearchTerm) {
        setUsers([]);
        return;
      }
      setIsSearchingUsers(true);
      const response = await sendRequest(
        `/users/lean/name/${encodeURIComponent(recipientSearchTerm)}`,
        'GET',
        null,
        { Authorization: `Bearer ${token}` }
      );
      setUsers(
        (response?.users || []).map((user) => ({
          value: user.id,
          label: user.fullName || user.fullNameReversed || user.email || '',
        }))
      );
    } catch (error) {
      setUsers([]);
    } finally {
      setIsSearchingUsers(false);
    }
  }, [recipientSearchTerm, token, sendRequest]);

  useEffect(() => {
    const timeoutHandler = setTimeout(() => {
      getUsers();
    }, SEARCH_DELAY);
    return () => clearTimeout(timeoutHandler);
  }, [recipientSearchTerm, getUsers]);

  const markReadHandler = async (messageId) => {
    await sendRequest(`/messages/${messageId}/read`, 'PATCH', null, {
      Authorization: `Bearer ${token}`,
    });
    setMessages((items) =>
      items.map((item) =>
        item.id === messageId || item._id === messageId ? { ...item, isRead: true } : item
      )
    );
    setSelectedMessage((current) =>
      current && (current.id === messageId || current._id === messageId)
        ? { ...current, isRead: true }
        : current
    );
    window.dispatchEvent(new Event(refreshEventName));
  };

  const markAllReadHandler = async () => {
    await sendRequest('/messages/readAll', 'PATCH', null, { Authorization: `Bearer ${token}` });
    setMessages((items) => items.map((item) => ({ ...item, isRead: true })));
    window.dispatchEvent(new Event(refreshEventName));
  };

  const deleteHandler = async (messageId) => {
    await sendRequest(`/messages/${messageId}`, 'DELETE', null, {
      Authorization: `Bearer ${token}`,
    });
    setMessages((items) => items.filter((item) => item.id !== messageId && item._id !== messageId));
    window.dispatchEvent(new Event(refreshEventName));
  };

  const buildMessageLink = (item) => resolveMessageLink(item);

  const openLinkHandler = (item) => {
    const link = typeof item === 'string' ? item : buildMessageLink(item);
    if (!link || !isTrustedMessageLink(link)) {
      return;
    }
    if (link.startsWith('http')) {
      try {
        const url = new URL(link);
        if (url.origin === window.location.origin) {
          history.push(`${url.pathname}${url.search}${url.hash}`);
          return;
        }
      } catch {
        return;
      }
      window.open(link, '_blank', 'noopener,noreferrer');
    } else {
      history.push(link);
    }
  };

  const openInboxMessage = async (item) => {
    setSelectedMessage(item);
    setDetailSource('inbox');
    if (!item.isRead) {
      try {
        await markReadHandler(item.id || item._id);
      } catch {
        // Snackbar is set by useAxios; keep the message open.
      }
    }
  };

  const openOutboxMessage = (item) => {
    setSelectedMessage(item);
    setDetailSource('outbox');
  };

  const handleDeleteFromDetail = async (messageId) => {
    await deleteHandler(messageId);
    setSelectedMessage(null);
  };

  const resetComposeForm = useCallback(() => {
    setTitle('');
    setMessage('');
    setSelectedRecipients([]);
    setSendToAll(false);
    setRecipientSearchTerm('');
    setIsRecipientsTouched(false);
    setUsers([]);
  }, []);

  const closeCompose = useCallback(() => {
    setIsComposeOpen(false);
    resetComposeForm();
  }, [resetComposeForm]);

  const handleSend = async () => {
    if (!isFormValid || !isRecipientsValid || !canSendMessages) {
      setIsRecipientsTouched(true);
      return;
    }

    await sendRequest(
      '/messages',
      'POST',
      {
        title: title.trim(),
        message: message.trim(),
        userIds: selectedRecipients.map((user) => user.value),
        sendToAll,
      },
      { Authorization: `Bearer ${token}` },
      'Odesílám zprávu . . .'
    );
    setIsComposeOpen(false);
    resetComposeForm();
    getOutboxMessages();
  };

  const showListChrome = selectedMessage === null && !isComposeOpen;
  const showComposeFab = token && activeTab === 1 && selectedMessage === null && !isComposeOpen;

  return (
    <>
      <Container sx={{ py: 1.5 }} maxWidth="sm">
        {selectedMessage ? (
          <MessageDetailView
            item={selectedMessage}
            source={detailSource}
            onBack={() => setSelectedMessage(null)}
            onOpenLink={openLinkHandler}
            onDelete={handleDeleteFromDetail}
          />
        ) : isComposeOpen ? (
          <MessageComposeView
            onBack={closeCompose}
            onSend={handleSend}
            canSendMessages={canSendMessages}
            isFormValid={isFormValid}
            isRecipientsValid={isRecipientsValid}
            isRecipientsTouched={isRecipientsTouched}
            users={users}
            selectedRecipients={selectedRecipients}
            onRecipientsChange={(value) => {
              setSelectedRecipients(value);
              setIsRecipientsTouched(true);
            }}
            recipientSearchTerm={recipientSearchTerm}
            onRecipientSearchChange={setRecipientSearchTerm}
            onRecipientsBlur={() => setIsRecipientsTouched(true)}
            sendToAll={sendToAll}
            onSendToAllChange={(nextValue) => {
              setSendToAll(nextValue);
              if (nextValue) {
                setSelectedRecipients([]);
                setIsRecipientsTouched(false);
              }
            }}
            isSearchingUsers={isSearchingUsers}
            title={title}
            onTitleChange={setTitle}
            message={message}
            onMessageChange={setMessage}
          />
        ) : (
          <>
            <Box className="glass-surface-accent glass-messages-tabs">
              <Tabs
                value={activeTab}
                onChange={(_, value) => setActiveTab(value)}
                variant="fullWidth"
                sx={tabSx}
              >
                <Tab label={`Přijaté (${unreadCount})`} />
                <Tab label="Odeslané" />
              </Tabs>
            </Box>

            {showListChrome && activeTab === 0 && (
              <Box
                sx={{
                  mb: 1.25,
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 0.75,
                }}
              >
                <Chip size="small" variant="outlined" label={`Celkem: ${messages.length}`} />
                <Chip size="small" variant="outlined" label={`Nepřečteno: ${unreadCount}`} />
                {filterDate && (
                  <Chip
                    size="small"
                    label={`Den: ${format(new Date(`${filterDate}T12:00:00`), 'd.M.yyyy')}`}
                    onDelete={() => history.push('/messages')}
                  />
                )}
                {activeTab === 0 && (
                  <Tooltip title="Vše přečtené" placement="bottom">
                    <span style={{ marginLeft: 'auto' }}>
                      <IconButton
                        size="small"
                        onClick={markAllReadHandler}
                        disabled={!canMarkAllRead}
                        sx={{
                          color: '#f05a5a',
                          '&.Mui-disabled': {
                            color: 'var(--ink-4)',
                            opacity: 0.45,
                          },
                          '&:hover:not(.Mui-disabled)': {
                            background: 'rgb(240 90 90 / 0.12)',
                          },
                        }}
                      >
                        <DoneAll fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                )}
              </Box>
            )}

            {activeTab === 0 && (
              <>
                {filteredMessages.length === 0 && (
                  <Typography variant="body2" sx={{ color: 'var(--ink-3)' }}>
                    {filterDate ? 'Pro tento den nemáte žádné zprávy.' : 'Žádné zprávy.'}
                  </Typography>
                )}
                <MessageListGrouped
                  groups={inboxGroups}
                  getSenderBadge={getInboxSenderLabel}
                  isUnread={(item) => !item.isRead}
                  onItemClick={openInboxMessage}
                />
              </>
            )}

            {activeTab === 1 && (
              <Box>
                {outboxMessages.length === 0 && (
                  <Typography variant="body2" sx={{ color: 'var(--ink-3)' }}>
                    Žádné odeslané zprávy.
                  </Typography>
                )}

                <MessageListGrouped
                  groups={outboxGroups}
                  getSenderBadge={(item) => {
                    const batchId = item.id || item._id;
                    const preview = recipientsCache[batchId] || item.recipientsPreview || [];
                    return getOutboxSenderBadge({ ...item, recipientsPreview: preview });
                  }}
                  onItemClick={openOutboxMessage}
                />
              </Box>
            )}
          </>
        )}
      </Container>

      {showComposeFab &&
        createPortal(
          <IconButton
            type="button"
            className="glass-compose-fab glass-accent-fill"
            onClick={() => setIsComposeOpen(true)}
            aria-label="Nová zpráva"
          >
            <Edit />
          </IconButton>,
          document.body
        )}

      <Snackbar
        open={!!requestStatus}
        autoHideDuration={requestStatus?.duration || 4000}
        onClose={clearRequestStatus}
      >
        {requestStatus && (
          <Alert
            onClose={clearRequestStatus}
            severity={requestStatus.severity || 'info'}
            sx={{
              backgroundColor: 'var(--glass-panel-veil)',
              color: 'var(--ink)',
              border: '1px solid var(--glass-border)',
              backdropFilter: 'blur(12px)',
            }}
          >
            {requestStatus.message}
          </Alert>
        )}
      </Snackbar>

      {loadingStatus.isLoading && (
        <Snackbar open={true}>
          <Alert
            severity="info"
            sx={{
              backgroundColor: 'var(--glass-panel-veil)',
              color: 'var(--ink)',
              border: '1px solid var(--glass-border)',
              backdropFilter: 'blur(12px)',
            }}
          >
            {loadingStatus.message || 'Načítám . . .'}
          </Alert>
        </Snackbar>
      )}
    </>
  );
};

export default Messages;
